import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  SvgFilters,
  ContentLoader,
  Content,
  ApiErrorComponentLazyLoad,
} from '@edikk-edikk/ui';
import { useErrorBoundary } from 'react-error-boundary';
import { ReCaptcha } from '@edikk-edikk/recaptcha';
import {
  ConnectGoogleAnalytics,
  SettingsContext,
  SettingsContextType,
  UserContext,
  UserContextType,
} from '@edikk-edikk/shared';
import loadable from '@loadable/component';
import { MenuWrapper } from '#baseMenu/containers/MenuWrapper';
import { useApi } from '#hooks/use-api';
import { BaseContext } from '#base/contexts/BaseContext';
import { BaseContextType } from '#base/contexts/BaseContextType';
import { Routers } from '#base/components/Routers';
import { AgeGateWrapper } from '#baseAgeGate/containers/AgeGateWrapper';
import { Footer } from '#base/components';
import { NotifierWrapper } from '#base/components/NotifierWrapper';

const ApiErrorComponent = loadable(() => import('@edikk-edikk/ui/error/api/ApiErrorComponent'), {
  resolveComponent: ({ ApiErrorComponent: Component }) => Component,
});

const App: FC = () => {
  const [isInit, setIsInit] = useState<boolean>(false);
  const location = useLocation();
  const api = useApi();
  const {
    data,
    isFetching,
    isError,
    error,
  } = api.getApiBase().useInitQuery(location.pathname, { skip: isInit });
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    setIsInit(!isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (error) {
      showBoundary(error);
    }
  }, [error, showBoundary]);

  if (isFetching || isError) {
    return <ContentLoader />;
  }

  const baseContextValue: BaseContextType = {
    routerConfig: data.routerConfig,
    language: data.language,
    seo: data.seo,
    catalogMode: data.catalogMode,
    footerLinks: data.footerLinks,
    ageGate: data.ageGate,
    mainImageUrl: data.mainImageUrl,
    hasPopularCityList: data.hasPopularCityList,
    hasCityList: data.hasCityList,
  };

  const userContextValue: UserContextType = {
    isAuth: data.isAuth,
  };

  const settingsContextValue: SettingsContextType = {
    ...data.settings,
  };

  return (
    <BaseContext.Provider value={baseContextValue}>
      <UserContext.Provider value={userContextValue}>
        <SettingsContext.Provider value={settingsContextValue}>
          <AgeGateWrapper />
          <ConnectGoogleAnalytics config={data.googleAnalyticsConfig} />
          <ReCaptcha isEnable={data.settings.reCaptcha.isEnable} site={data.settings.reCaptcha.site}>
            <NotifierWrapper />
            <SvgFilters />
            <Content>
              <Routers />
              <ApiErrorComponentLazyLoad ApiErrorComponent={ApiErrorComponent} />
              <Footer />
            </Content>
            <MenuWrapper />
          </ReCaptcha>
        </SettingsContext.Provider>
      </UserContext.Provider>
    </BaseContext.Provider>
  );
};

export { App };
